/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1680307200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Sat Apr 01 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "statsig",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Statsig",
          "depth": 0,
          "id": "connect-to-statsig"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Segments",
          "depth": 1,
          "id": "segments"
        }, {
          "value": "Events",
          "depth": 1,
          "id": "events"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Rate limits",
          "depth": 1,
          "id": "rate-limits"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger-1"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4",
      img: "img",
      code: "code"
    }, _provideComponents(), props.components), {PylonLink, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Sync to segments and events from your warehouse to Statsig to analyze product performance and run experiments."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Segments"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to segments in Statsig"
            }), _jsx(_components.td, {
              children: "Add, Remove"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to log events in Statsig"
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-statsig",
        children: "Connect to Statsig"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), "\nbutton. Select ", _jsx(_components.strong, {
          children: "Statsig"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Statsig"
        }), " by entering your credentials."]
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields into Hightouch based on the types of syncs you plan on creating:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Console API key"
          }), ": required for syncing segments"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Client-SDK API key"
          }), ": required for syncing events"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can find these API keys in the ", _jsx(_components.strong, {
          children: "Keys & Environments"
        }), " section on your Statsig settings page. Check out Statsig's ", _jsx(_components.a, {
          href: "https://docs.statsig.com/console-api/introduction",
          children: "Console API docs"
        }), " and ", _jsx(_components.a, {
          href: "https://docs.statsig.com/http-api",
          children: "HTTP API docs"
        }), " for more information about how to create and access these keys."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Statsig destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull\ndata from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview\npage"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and\nthe Statsig destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "segments",
        children: "Segments"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync data from any source to Statsig ", _jsx(_components.a, {
          href: "https://docs.statsig.com/console-api/segments",
          children: "segments"
        }), ". Hightouch only supports syncing to the ID list segment type."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch matches rows from your model to Statsig segments by segment ", _jsx(_components.strong, {
          children: "id"
        }), ". Select the model column that contains these values."]
      }), "\n", _jsx(_components.h4, {
        id: "create-a-segment",
        children: "Create a segment"
      }), "\n", _jsxs(_components.p, {
        children: ["To create a new segment list, select ", _jsx(_components.strong, {
          children: "Create a new audience"
        }), ". You can provide a name for this segment, or Hightouch defaults to the name of the model used to create the sync."]
      }), "\n", _jsxs(_components.p, {
        children: ["Then, select the ", _jsx(_components.strong, {
          children: "ID type"
        }), " of the segment list you want Hightouch to create. Hightouch supports user ID and stable ID types.\nYou can change the ID type to a ", _jsx(_components.a, {
          href: "https://docs.statsig.com/guides/experiment-on-custom-id-types",
          children: "custom ID"
        }), " on the segment's page in Statsig."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-statsig-edit-type.png",
          alt: "Editing ID type in Statsig"
        })
      }), "\n", _jsx(_components.h4, {
        id: "use-an-existing-segment",
        children: "Use an existing segment"
      }), "\n", _jsxs(_components.p, {
        children: ["To use an existing segment list, select ", _jsx(_components.strong, {
          children: "Use existing segment"
        }), ". You can select any of your existing Statsig segments from the drop-down select menu. All available options are of the ", _jsx(_components.strong, {
          children: "ID list"
        }), " type."]
      }), "\n", _jsx(_components.h3, {
        id: "events",
        children: "Events"
      }), "\n", _jsxs(_components.p, {
        children: ["Sync data from any source to ", _jsx(_components.a, {
          href: "https://docs.statsig.com/guides/logging-events",
          children: "events"
        }), " in Statsig."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Statsig's ", _jsx(_components.a, {
            href: "https://docs.statsig.com/events-explorer",
            children: "event explorer"
          }), " requires you to be on a ", _jsx(_components.a, {
            href: "https://www.statsig.com/pricing",
            children: "Pro Plan"
          }), ". You won't be able to view events you send via Hightouch in the Statsig app without it."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "event-name",
        children: "Event name"
      }), "\n", _jsxs(_components.p, {
        children: ["Providing an event name is required to log an event in Statsig. You can either provide a static value or select to ", _jsx(_components.strong, {
          children: "use a column"
        }), " from your model."]
      }), "\n", _jsx(_components.h4, {
        id: "event-timestamp",
        children: "Event timestamp"
      }), "\n", _jsx(_components.p, {
        children: "You can optionally select a column that contains timestamps of when events occurred. If this field is empty, Hightouch uses the time the event arrives at the server."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch converts any ", _jsx(_components.code, {
          children: "DateTime"
        }), " values from your source to Unix timestamp as expected by Statsig."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to different event and user attributes in the mappings section."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "rate-limits",
        children: "Rate limits"
      }), "\n", _jsxs(_components.p, {
        children: ["Statsig has a default rate limit of 10 requests/second and 4500 request/15 minutes on Console API. If you're syncing segments and run into a rate limit error, ", _jsx(_components.a, {
          href: "/syncs/schedule-sync-ui",
          children: "schedule"
        }), " your sync to run less frequently or ", _jsx(PylonLink, {
          pylonText: "",
          linkText: "get in touch"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch provides complete visibility into the API calls made during each of your sync runs. We recommend reading our\n", _jsx(_components.a, {
          href: "/syncs/debugger",
          children: "article on debugging tips and tricks"
        }), " to learn more."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger-1",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
